import { useCallback, useMemo } from 'react';
import { useUpdateActivityMutation } from '@hooks/index';
import { useHandleActivityState } from '@pages/activityBuilder/hooks/useHandleActivityState';
import { useAppParams } from '@app/hooks';
import { useFirebaseHook, EventTypes } from '@utils/firebaseUtils';
import useErrorMessage from '@utils/useErrorMessage';
import { biodiversityActivityValidationSchema, type BiodiversityActivityFormValues } from '@pages/project/activities/biodiversityActivityForm';

export const useEditBiodiversityActivity = ({
	setIsSuccessModalOpen,
}: {
	setIsSuccessModalOpen: (isOpen: boolean) => void;
}) => {
	const { activityId } = useAppParams();
	const { activity, isFetching, isError, error } = useHandleActivityState();
	const { fireEvent } = useFirebaseHook();

	const [updateActivity, { isLoading: isUpdateLoading, isError: isUpdateError, error: updateError }] =
		useUpdateActivityMutation();

	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { errorMsg: updateErrorMessage } = useErrorMessage({ isError: isUpdateError, error: updateError });

	// Guard against stale Redux data from a previously viewed activity
	const isActivityMatch = activity?.id === Number(activityId);

	const initialValues: BiodiversityActivityFormValues = useMemo(() => {
		if (!isActivityMatch) {
			return {
				activityTitle: '',
				startYear: new Date().getFullYear(),
				endYear: new Date().getFullYear() + 5,
			};
		}
		return {
			activityTitle: activity?.name ?? '',
			startYear: activity?.start_year_t2 ?? new Date().getFullYear(),
			endYear: activity?.last_year_of_accounting_t2 ?? new Date().getFullYear() + 5,
		};
	}, [activity, isActivityMatch]);

	const validationSchema = biodiversityActivityValidationSchema;

	const handleSubmit = useCallback(async (values: BiodiversityActivityFormValues) => {
		if (!activity?.id) return;
		try {
			await updateActivity({
				id: activity.id,
				activity: {
					name: values.activityTitle,
					start_year_t2: values.startYear,
					last_year_of_accounting_t2: values.endYear,
				},
			}).unwrap();
			fireEvent(EventTypes.edit_activity);
			setIsSuccessModalOpen(true);
		} catch (err) {
			console.error(err);
		}
	}, [activity, updateActivity, fireEvent, setIsSuccessModalOpen]);

	return {
		activity,
		initialValues,
		validationSchema,
		handleSubmit,
		isUpdateLoading,
		isUpdateError,
		updateErrorMessage,
		blockingError,
		isFetching,
	};
};
